import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchEarliestDeliveryDatesMap } from "../../../utils/pdp/pdpWidgetUtils";

const DELIVERY_VISIBILITY_THRESHOLD = 0.1;

/**
 * Fetches earliest delivery dates when a widget enters the viewport.
 * Uses a callback ref and a scoped IntersectionObserver so late-mounted nodes
 * (e.g. trending widgets that render after data loads) are observed correctly.
 *
 * @param {Object} params
 * @param {Object[]} params.productsList
 * @param {string} [params.pincode]
 * @param {boolean} [params.isEnabled]
 * @param {Object} [params.latLong]
 * @param {number|string} [params.latLong.lat]
 * @param {number|string} [params.latLong.long]
 * @returns {{ resolveDeliveryEntry: (product: Object) => string|Object|null, sectionRef: (node: HTMLElement|null) => void }}
 */
function useWidgetDeliveryDates({ productsList, pincode, isEnabled = true, latLong = {} }) {
  const [deliveryDates, setDeliveryDates] = useState({});
  const [sectionNode, setSectionNode] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasFetchedRef = useRef(false);

  const sectionRef = useCallback((node) => {
    setSectionNode(node);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver || !sectionNode) {
      return undefined;
    }

    let hasFrozen = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }

        setIsVisible(entry.isIntersecting);

        if (entry.isIntersecting && !hasFrozen) {
          hasFrozen = true;
          observer.unobserve(sectionNode);
        }
      },
      { threshold: DELIVERY_VISIBILITY_THRESHOLD },
    );

    observer.observe(sectionNode);

    return () => {
      observer.disconnect();
    };
  }, [sectionNode]);

  const productIdsKey = useMemo(
    () =>
      productsList
        .map((product) => product?.productId)
        .filter(Boolean)
        .join(","),
    [productsList],
  );

  const fetchDeliveryDates = useCallback(async () => {
    if (!pincode || !productIdsKey || hasFetchedRef.current) {
      return;
    }

    hasFetchedRef.current = true;

    const dates = await fetchEarliestDeliveryDatesMap(pincode, latLong, productsList);

    if (Object.keys(dates).length > 0) {
      setDeliveryDates((prevDates) => ({ ...prevDates, ...dates }));
    }
  }, [latLong, pincode, productIdsKey, productsList]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [productIdsKey, pincode, latLong]);

  useEffect(() => {
    setDeliveryDates({});
  }, [pincode]);

  useEffect(() => {
    if (isEnabled && isVisible && pincode && productIdsKey) {
      fetchDeliveryDates();
    }
  }, [fetchDeliveryDates, isEnabled, isVisible, pincode, productIdsKey]);

  const resolveDeliveryEntry = useCallback(
    (product) => {
      if (!isEnabled) {
        return product?.deliveryEntry || null;
      }

      return deliveryDates[product?.productId] || product?.deliveryEntry || null;
    },
    [deliveryDates, isEnabled],
  );

  return { resolveDeliveryEntry, sectionRef };
}

export default useWidgetDeliveryDates;
